import { useState, useEffect } from 'react';
import { Shield, ShieldAlert, ShieldCheck, ChevronDown, ChevronUp, TrendingUp, TrendingDown } from 'lucide-react';
import axios from 'axios';

const API_V2 = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;

const fmtUsd = (n, d = 2) => {
  const v = Number(n || 0);
  return `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(d)}`;
};

const fmtCoin = (n) => Number(n || 0).toFixed(8);

const ProtectionSellAudit = ({ mode = 'simulation' }) => {
  const [trade, setTrade] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  const fetchAudit = async () => {
    try {
      const res = await axios.get(`${API_V2}/user/trades/last-protection-sell`);
      setTrade(res.data?.trade || null);
    } catch (err) {
      // 401 or other — silently fail (user might not be authed yet)
      setTrade(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudit();
    const interval = setInterval(fetchAudit, 30000); // refresh every 30s
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  if (loading || !trade) return null;

  const trigger = (trade.trigger || '').toUpperCase();
  const isTP = trigger === 'TAKE_PROFIT';
  const isSL = trigger === 'STOP_LOSS';
  const breakdown = Array.isArray(trade.lots_breakdown) ? trade.lots_breakdown : [];
  const profit = Number(trade.profit || 0);
  const profitable = profit > 0;
  const ts = trade.timestamp ? new Date(trade.timestamp) : null;
  const timeStr = ts
    ? ts.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : 'N/A';

  // Theme based on trigger
  const triggerColor = isTP ? 'text-[#00FF41]' : isSL ? 'text-[#FF4444]' : 'text-[#00FF41]';
  const triggerBg = isTP ? 'border-[#00FF41]' : isSL ? 'border-[#FF4444]' : 'border-[#00FF41]';
  const Icon = isSL ? ShieldAlert : ShieldCheck;

  return (
    <div
      className={`mt-4 bg-[#0a0a0a] border ${triggerBg} rounded-md overflow-hidden`}
      data-testid="protection-sell-audit"
    >
      {/* Header */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-[#0f0f0f] transition-colors"
        data-testid="protection-audit-toggle"
      >
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${triggerColor}`} />
          <div className="text-left">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-sm font-bold uppercase tracking-wider ${triggerColor}`}>
                Protection Audit
              </span>
              <span className={`text-[10px] px-2 py-0.5 border ${triggerBg} ${triggerColor} uppercase tracking-widest font-mono`}>
                {trigger || 'UNKNOWN'}
              </span>
              <span className="text-[10px] px-2 py-0.5 border border-[#003B00] text-[#008F11] uppercase tracking-widest font-mono">
                {trade.pair}
              </span>
              <span className="text-[10px] text-[#008F11] font-mono uppercase">
                {trade.mode === 'live' ? 'LIVE' : 'SIM'}
              </span>
            </div>
            <div className="text-[11px] text-[#008F11] mt-0.5">
              Last triggered: {timeStr} • {breakdown.length} lot{breakdown.length === 1 ? '' : 's'} sold
              {trade.lots_remaining != null && (
                <span className="text-[#666]"> • {trade.lots_remaining} preserved</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className={`text-sm font-bold font-mono ${profitable ? 'text-[#00FF41]' : 'text-[#FF4444]'}`}>
              {profitable ? '+' : ''}{fmtUsd(profit)}
            </div>
            <div className="text-[10px] text-[#666] uppercase tracking-wider">Net P/L</div>
          </div>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-[#008F11]" />
          ) : (
            <ChevronDown className="w-4 h-4 text-[#008F11]" />
          )}
        </div>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-[#003B00]/50" data-testid="protection-audit-body">
          {/* Summary strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 my-3 text-[11px]">
            <div className="bg-[#000] border border-[#003B00] p-2">
              <div className="text-[#666] uppercase tracking-wider">Sell Price</div>
              <div className="text-[#00FF41] font-mono font-bold">{fmtUsd(trade.price)}</div>
            </div>
            <div className="bg-[#000] border border-[#003B00] p-2">
              <div className="text-[#666] uppercase tracking-wider">Amount</div>
              <div className="text-[#00FF41] font-mono font-bold">{fmtCoin(trade.amount)}</div>
            </div>
            <div className="bg-[#000] border border-[#003B00] p-2">
              <div className="text-[#666] uppercase tracking-wider">Gross USD</div>
              <div className="text-[#00FF41] font-mono font-bold">{fmtUsd(trade.usd_value)}</div>
            </div>
            <div className="bg-[#000] border border-[#003B00] p-2">
              <div className="text-[#666] uppercase tracking-wider">Lots Sold</div>
              <div className="text-[#00FF41] font-mono font-bold">
                {breakdown.length}{trade.lots_remaining != null ? ` / ${breakdown.length + trade.lots_remaining}` : ''}
              </div>
            </div>
          </div>

          {/* Per-lot table */}
          {breakdown.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] font-mono">
                <thead>
                  <tr className="text-[#666] uppercase tracking-wider border-b border-[#003B00]">
                    <th className="text-left py-2 px-1">Buy @</th>
                    <th className="text-right py-2 px-1">Amount</th>
                    <th className="text-right py-2 px-1">Cost</th>
                    <th className="text-right py-2 px-1">Gain</th>
                    <th className="text-right py-2 px-1">Gross P/L</th>
                    <th className="text-right py-2 px-1">Fee Est</th>
                    <th className="text-right py-2 px-1">Net P/L</th>
                  </tr>
                </thead>
                <tbody data-testid="protection-audit-lots">
                  {breakdown.map((lot, i) => {
                    const gross = Number(lot.gross_profit || 0);
                    const fee = Number(lot.fee_est || 0);
                    const net = gross - fee;
                    const gainPct = Number(lot.gain_pct || 0);
                    const isUp = gainPct >= 0;
                    return (
                      <tr
                        key={i}
                        className="border-b border-[#003B00]/30 hover:bg-[#0f0f0f]"
                        data-testid={`protection-audit-lot-${i}`}
                      >
                        <td className="py-2 px-1 text-[#00FF41]">{fmtUsd(lot.buy_price, 4)}</td>
                        <td className="py-2 px-1 text-right text-[#008F11]">{fmtCoin(lot.amount)}</td>
                        <td className="py-2 px-1 text-right text-[#008F11]">{fmtUsd(lot.cost)}</td>
                        <td className={`py-2 px-1 text-right ${isUp ? 'text-[#00FF41]' : 'text-[#FF4444]'}`}>
                          <span className="inline-flex items-center gap-1 justify-end">
                            {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {isUp ? '+' : ''}{gainPct.toFixed(2)}%
                          </span>
                        </td>
                        <td className={`py-2 px-1 text-right ${gross >= 0 ? 'text-[#00FF41]' : 'text-[#FF4444]'}`}>
                          {gross >= 0 ? '+' : ''}{fmtUsd(gross)}
                        </td>
                        <td className="py-2 px-1 text-right text-[#666]">-{fmtUsd(fee)}</td>
                        <td className={`py-2 px-1 text-right font-bold ${net >= 0 ? 'text-[#00FF41]' : 'text-[#FF4444]'}`}>
                          {net >= 0 ? '+' : ''}{fmtUsd(net)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-[11px] text-[#666] py-3 italic">
              No per-lot breakdown available for this trade (legacy record).
            </div>
          )}

          <div className="text-[10px] text-[#666] mt-3 italic flex items-center gap-2">
            <Shield className="w-3 h-3" />
            v3.9.6+ per-lot logic — only qualifying lots were sold. Unsold lots remain in your buy_lots ledger.
          </div>
        </div>
      )}
    </div>
  );
};

export default ProtectionSellAudit;
